import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormField, form } from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import {
  IPideloAdminData,
  IPideloCategoriaAdmin,
  IPideloConfig,
  IPideloEmpresaDefaults,
  IPideloProductoAdmin,
} from '@core/interfaces/entidades/pidelo.entidad';
import { PideloApiService } from '@core/services/api/pidelo-api.service';
import { environment } from '@env/environment';
import { PideloImagenService } from '../services/pidelo-imagen.service';
import { InputBuscador } from '@shared/components/input-buscador/input-buscador';
import { UiButton, UiTabs, UiTag } from '@shared/components/ui';
import type { TabItem } from '@shared/components/ui';
import { AppService } from '@shared/services/app.service';
import { ToastService } from '@shared/services/toast.service';

type FiltroVisibilidad = 'todos' | 'visibles' | 'ocultos';
type ColorPill = 'green' | 'amber' | 'violet';

@Component({
  selector: 'app-pidelo-admin',
  imports: [FormField, InputBuscador, RouterLink, UiButton, UiTabs, UiTag],
  templateUrl: './pidelo-admin.html',
  styleUrl: './pidelo-admin.css',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PideloAdmin implements OnInit {
  private readonly api = inject(PideloApiService);
  private readonly toast = inject(ToastService);
  private readonly appService = inject(AppService);
  private readonly imagenes = inject(PideloImagenService);

  private cargandoDatos = false;

  readonly cargando = signal(true);
  readonly guardando = signal(false);
  readonly empresa = signal<IPideloEmpresaDefaults | null>(null);
  readonly config = signal<IPideloConfig | null>(null);
  readonly categorias = signal<IPideloCategoriaAdmin[]>([]);
  readonly productos = signal<IPideloProductoAdmin[]>([]);

  readonly tab = signal('carta');
  readonly tabsList: TabItem[] = [
    { value: 'carta', label: 'Mi carta', icon: 'bi bi-card-list' },
    { value: 'productos', label: 'Productos', icon: 'bi bi-box-seam' },
    { value: 'categorias', label: 'Categorías', icon: 'bi bi-tags' },
  ];

  readonly busqueda = signal('');
  readonly filtro = signal<FiltroVisibilidad>('todos');
  readonly filtros: { label: string; value: FiltroVisibilidad }[] = [
    { label: 'Todos', value: 'todos' },
    { label: 'Visibles', value: 'visibles' },
    { label: 'Ocultos', value: 'ocultos' },
  ];

  readonly configModel = signal({
    nombre_comercial: '',
    descripcion: '',
    logo_url: '',
    banner_url: '',
    whatsapp: '',
    direccion: '',
    moneda: 'PEN',
    delivery_habilitado: true,
    recojo_habilitado: true,
    costo_delivery_habilitado: false,
    costo_delivery: 0,
    pedido_minimo: 0,
    mensaje_bienvenida: '',
    mensaje_pedido: '',
    publicado: false,
  });

  readonly configForm = form(this.configModel);

  readonly enlacePublico = computed(() => {
    const endpoint = this.empresa()?.pidelo_endpoint?.trim();
    if (endpoint) return endpoint;

    const slug = this.config()?.slug;
    if (!slug) return '';
    return `${environment.pideloPublicUrl.replace(/\/$/, '')}/${slug}`;
  });

  readonly whatsappEfectivo = computed(
    () => this.configModel().whatsapp.trim() || this.empresa()?.telefono?.trim() || ''
  );

  readonly empresaNombre = computed(
    () => this.configModel().nombre_comercial.trim() || this.empresa()?.nombre || 'Mi empresa'
  );

  readonly logoPreview = computed(() => {
    const propio = this.configModel().logo_url.trim();
    return this.imagenes.resolver(propio || this.empresa()?.logo_url);
  });

  readonly bannerPreview = computed(() =>
    this.imagenes.resolver(this.configModel().banner_url.trim())
  );

  readonly productosVisibles = computed(
    () => this.productos().filter((producto) => producto.visible && producto.activo).length
  );

  readonly categoriasVisibles = computed(
    () => this.categorias().filter((categoria) => categoria.visible).length
  );

  readonly deliveryResumen = computed(() => {
    if (!this.configModel().delivery_habilitado) return 'No disponible';
    if (!this.configModel().costo_delivery_habilitado) return 'A coordinar';
    return this.moneda(Number(this.configModel().costo_delivery) || 0);
  });

  readonly costoDeliveryPreview = computed(() => {
    if (!this.configModel().costo_delivery_habilitado) return 'A coordinar';
    const valor = Number(this.configModel().costo_delivery) || 0;
    return valor > 0 ? this.moneda(valor) : 'Gratis';
  });

  readonly productosFiltrados = computed(() => {
    const consulta = this.busqueda().trim().toLowerCase();
    const filtro = this.filtro();

    return this.productos().filter((producto) => {
      if (filtro === 'visibles' && !producto.visible) return false;
      if (filtro === 'ocultos' && producto.visible) return false;
      if (
        consulta &&
        !`${producto.nombre} ${producto.categoria_nombre ?? ''}`.toLowerCase().includes(consulta)
      ) {
        return false;
      }
      return true;
    });
  });

  constructor() {
    effect(() => {
      if (this.appService.authReady()) {
        this.cargar();
      }
    });
  }

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Pidelo',
      subtitle: 'Tu carta digital para recibir pedidos por WhatsApp',
      showBackButton: true,
      icon: 'bi bi-phone',
    });
  }

  cargar(): void {
    if (this.cargandoDatos) return;
    this.cargandoDatos = true;
    this.cargando.set(true);

    this.api.cargarAdmin().subscribe({
      next: (data) => {
        this.aplicarDatos(data);
        this.cargando.set(false);
        this.cargandoDatos = false;
      },
      error: (error) => {
        this.cargando.set(false);
        this.cargandoDatos = false;
        this.toast.show(
          'top-right',
          'error',
          'Pidelo',
          this.mensajeError(error, 'No se pudo cargar la configuración de la carta.')
        );
      },
    });
  }

  guardar(): void {
    const valores = this.configModel();

    if (!valores.delivery_habilitado && !valores.recojo_habilitado) {
      this.toast.show(
        'top-right',
        'warn',
        'Revisar modalidades',
        'Habilita al menos delivery o recojo.'
      );
      return;
    }

    if (valores.publicado && !this.whatsappEfectivo()) {
      this.toast.show(
        'top-right',
        'warn',
        'Falta WhatsApp',
        'Configura un número de WhatsApp antes de publicar la carta.'
      );
      return;
    }

    this.guardando.set(true);

    this.api
      .guardarConfig({
        nombre_comercial: valores.nombre_comercial.trim() || null,
        descripcion: valores.descripcion.trim() || null,
        logo_url: valores.logo_url.trim() || null,
        banner_url: valores.banner_url.trim() || null,
        whatsapp: valores.whatsapp.trim() || null,
        direccion: valores.direccion.trim() || null,
        moneda: valores.moneda || 'PEN',
        delivery_habilitado: valores.delivery_habilitado,
        recojo_habilitado: valores.recojo_habilitado,
        costo_delivery_habilitado: valores.costo_delivery_habilitado,
        costo_delivery: Number(valores.costo_delivery) || 0,
        pedido_minimo: Number(valores.pedido_minimo) || 0,
        mensaje_bienvenida: valores.mensaje_bienvenida.trim() || null,
        mensaje_pedido: valores.mensaje_pedido.trim() || null,
        publicado: valores.publicado,
      })
      .subscribe({
        next: (config) => {
          this.config.set(config);
          this.aplicarConfig(config);
          this.guardando.set(false);
          this.toast.show(
            'top-right',
            'success',
            'Guardado',
            config.publicado ? 'Tu carta ya está publicada.' : 'La configuración se guardó como borrador.'
          );
        },
        error: (error) => {
          this.guardando.set(false);
          this.toast.show(
            'top-right',
            'error',
            'Pidelo',
            this.mensajeError(error, 'No se pudo guardar la configuración.')
          );
        },
      });
  }

  copiarEnlace(): void {
    const url = this.enlacePublico();
    if (!url) {
      this.toast.show(
        'top-right',
        'warn',
        'Enlace pendiente',
        'Guarda la configuración para generar el enlace público.'
      );
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      this.toast.show('top-right', 'info', 'Copia el enlace', url);
      return;
    }

    navigator.clipboard.writeText(url).then(
      () => this.toast.show('top-right', 'success', 'Enlace copiado', url),
      () => this.toast.show('top-right', 'warn', 'Copia el enlace', url)
    );
  }

  setFiltro(filtro: FiltroVisibilidad): void {
    this.filtro.set(filtro);
  }

  conteoFiltro(filtro: FiltroVisibilidad): number {
    const productos = this.productos();
    if (filtro === 'visibles') return productos.filter((producto) => producto.visible).length;
    if (filtro === 'ocultos') return productos.filter((producto) => !producto.visible).length;
    return productos.length;
  }

  setCostoDeliveryHabilitado(valor: boolean): void {
    this.configModel.update((actual) => ({ ...actual, costo_delivery_habilitado: valor }));
  }

  modoDeliveryClass(activo: boolean): string {
    const base =
      'rounded-xl border p-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/30';
    return activo
      ? `${base} border-accent-500 bg-accent-50 ring-1 ring-accent-500/20`
      : `${base} border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50`;
  }

  abrirCarta(): void {
    const url = this.enlacePublico();
    if (!url || typeof window === 'undefined') {
      this.toast.show(
        'top-right',
        'warn',
        'Enlace pendiente',
        'Guarda la configuración para generar el enlace público.'
      );
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  toggleProducto(
    producto: IPideloProductoAdmin,
    campo: 'visible' | 'disponible' | 'destacado',
    valor: boolean
  ): void {
    if (campo === 'visible' && valor && !producto.activo) {
      this.toast.show(
        'top-right',
        'warn',
        'Producto inactivo',
        'Activa el producto en Gestión → Productos para poder publicarlo.'
      );
      return;
    }

    const anterior = producto;
    this.productos.update((lista) =>
      lista.map((item) => (item.id === producto.id ? { ...item, [campo]: valor } : item))
    );

    this.api.actualizarProducto(producto.id, { [campo]: valor }).subscribe({
      next: (actualizado) => {
        this.productos.update((lista) =>
          lista.map((item) => (item.id === producto.id ? { ...item, ...actualizado } : item))
        );
      },
      error: (error) => {
        this.productos.update((lista) =>
          lista.map((item) => (item.id === producto.id ? anterior : item))
        );
        this.toast.show(
          'top-right',
          'error',
          'Producto',
          this.mensajeError(error, 'No se pudo actualizar el producto.')
        );
      },
    });
  }

  cambiarPrecio(producto: IPideloProductoAdmin, evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const texto = input.value.trim();
    const precio = texto === '' ? null : Number(texto);

    if (precio !== null && (!Number.isFinite(precio) || precio < 0)) {
      input.value = producto.precio_publico?.toString() ?? '';
      this.toast.show('top-right', 'warn', 'Precio inválido', 'Ingresa un precio mayor o igual a 0.');
      return;
    }

    this.guardarPrecio(producto, precio);
  }

  restablecerPrecio(producto: IPideloProductoAdmin): void {
    this.guardarPrecio(producto, null);
  }

  cambiarOrden(producto: IPideloProductoAdmin, evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const orden = Math.trunc(Number(input.value));

    if (!Number.isFinite(orden) || orden < 0 || orden > 9999) {
      input.value = producto.orden.toString();
      this.toast.show('top-right', 'warn', 'Orden inválido', 'Usa un número entre 0 y 9999.');
      return;
    }

    const anterior = producto;
    this.productos.update((lista) =>
      lista.map((item) => (item.id === producto.id ? { ...item, orden } : item))
    );

    this.api.actualizarProducto(producto.id, { orden }).subscribe({
      next: (actualizado) => {
        this.productos.update((lista) =>
          lista.map((item) => (item.id === producto.id ? { ...item, ...actualizado } : item))
        );
        this.ordenarProductos();
      },
      error: (error) => {
        this.productos.update((lista) =>
          lista.map((item) => (item.id === producto.id ? anterior : item))
        );
        this.toast.show(
          'top-right',
          'error',
          'Producto',
          this.mensajeError(error, 'No se pudo actualizar el orden.')
        );
      },
    });
  }

  toggleCategoria(categoria: IPideloCategoriaAdmin, valor: boolean): void {
    const anterior = categoria;
    this.categorias.update((lista) =>
      lista.map((item) => (item.id === categoria.id ? { ...item, visible: valor } : item))
    );

    this.api.actualizarCategoria(categoria.id, { visible: valor }).subscribe({
      next: (actualizada) => {
        this.categorias.update((lista) =>
          lista.map((item) => (item.id === categoria.id ? { ...item, ...actualizada } : item))
        );
      },
      error: (error) => {
        this.categorias.update((lista) =>
          lista.map((item) => (item.id === categoria.id ? anterior : item))
        );
        this.toast.show(
          'top-right',
          'error',
          'Categoría',
          this.mensajeError(error, 'No se pudo actualizar la categoría.')
        );
      },
    });
  }

  cambiarOrdenCategoria(categoria: IPideloCategoriaAdmin, evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const orden = Math.trunc(Number(input.value));

    if (!Number.isFinite(orden) || orden < 0 || orden > 9999) {
      input.value = categoria.orden.toString();
      this.toast.show('top-right', 'warn', 'Orden inválido', 'Usa un número entre 0 y 9999.');
      return;
    }

    const anterior = categoria;
    this.categorias.update((lista) =>
      lista.map((item) => (item.id === categoria.id ? { ...item, orden } : item))
    );

    this.api.actualizarCategoria(categoria.id, { orden }).subscribe({
      next: (actualizada) => {
        this.categorias.update((lista) =>
          lista.map((item) => (item.id === categoria.id ? { ...item, ...actualizada } : item))
        );
        this.productos.update((lista) =>
          lista.map((item) =>
            item.categoria_id === categoria.id ? { ...item, categoria_orden: actualizada.orden } : item
          )
        );
        this.ordenarCategorias();
        this.ordenarProductos();
      },
      error: (error) => {
        this.categorias.update((lista) =>
          lista.map((item) => (item.id === categoria.id ? anterior : item))
        );
        this.toast.show(
          'top-right',
          'error',
          'Categoría',
          this.mensajeError(error, 'No se pudo actualizar el orden.')
        );
      },
    });
  }

  chipClass(activo: boolean): string {
    const base = 'shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition';
    return activo
      ? `${base} border-accent-600 bg-accent-600 text-white`
      : `${base} border-slate-200 bg-white text-slate-600 hover:bg-slate-50`;
  }

  pillClass(activo: boolean, color: ColorPill): string {
    const base = 'rounded-full px-2.5 py-1 text-[11px] font-semibold transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-50';
    if (!activo) return `${base} bg-slate-100 text-slate-400 hover:bg-slate-200`;
    const colores: Record<ColorPill, string> = {
      green: 'bg-teal-100 text-teal-700 hover:bg-teal-200',
      amber: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
      violet: 'bg-violet-100 text-violet-700 hover:bg-violet-200',
    };
    return `${base} ${colores[color]}`;
  }

  moneda(valor: number): string {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: this.configModel().moneda || 'PEN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(valor);
  }

  imagenProducto(url: string | null): string | null {
    return this.imagenes.resolver(url);
  }

  onImageError(evento: Event): void {
    const imagen = evento.target as HTMLImageElement;
    if (!imagen.src.endsWith('/img/no-image.jpg')) {
      imagen.src = '/img/no-image.jpg';
    }
  }

  private ordenarProductos(): void {
    this.productos.update((lista) =>
      [...lista].sort((a, b) => {
        const porCategoria = (a.categoria_orden ?? 0) - (b.categoria_orden ?? 0);
        if (porCategoria !== 0) return porCategoria;

        const nombreCategoria = (a.categoria_nombre ?? '\uffff').localeCompare(
          b.categoria_nombre ?? '\uffff',
          'es'
        );
        if (nombreCategoria !== 0) return nombreCategoria;

        const porOrden = a.orden - b.orden;
        if (porOrden !== 0) return porOrden;

        return a.nombre.localeCompare(b.nombre, 'es');
      })
    );
  }

  private ordenarCategorias(): void {
    this.categorias.update((lista) =>
      [...lista].sort(
        (a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre, 'es')
      )
    );
  }

  private guardarPrecio(producto: IPideloProductoAdmin, precio: number | null): void {
    const anterior = producto;
    const precioEfectivo = precio ?? producto.precio_venta;

    this.productos.update((lista) =>
      lista.map((item) =>
        item.id === producto.id
          ? { ...item, precio_publico: precio, precio_efectivo: precioEfectivo }
          : item
      )
    );

    this.api.actualizarProducto(producto.id, { precio_publico: precio }).subscribe({
      next: (actualizado) => {
        this.productos.update((lista) =>
          lista.map((item) => (item.id === producto.id ? { ...item, ...actualizado } : item))
        );
        this.toast.show(
          'top-right',
          'success',
          'Precio actualizado',
          precio === null ? 'Se usará el precio base del producto.' : producto.nombre
        );
      },
      error: (error) => {
        this.productos.update((lista) =>
          lista.map((item) => (item.id === producto.id ? anterior : item))
        );
        this.toast.show(
          'top-right',
          'error',
          'Producto',
          this.mensajeError(error, 'No se pudo actualizar el precio.')
        );
      },
    });
  }

  private aplicarDatos(data: IPideloAdminData): void {
    this.empresa.set(data.empresa);
    this.config.set(data.config);
    this.categorias.set(data.categorias);
    this.productos.set(data.productos);
    this.aplicarConfig(data.config);
  }

  private aplicarConfig(config: IPideloConfig | null): void {
    this.configModel.set({
      nombre_comercial: config?.nombre_comercial ?? '',
      descripcion: config?.descripcion ?? '',
      logo_url: config?.logo_url ?? '',
      banner_url: config?.banner_url ?? '',
      whatsapp: config?.whatsapp ?? '',
      direccion: config?.direccion ?? '',
      moneda: config?.moneda ?? 'PEN',
      delivery_habilitado: config?.delivery_habilitado ?? true,
      recojo_habilitado: config?.recojo_habilitado ?? true,
      costo_delivery_habilitado: config?.costo_delivery_habilitado ?? false,
      costo_delivery: Number(config?.costo_delivery ?? 0),
      pedido_minimo: Number(config?.pedido_minimo ?? 0),
      mensaje_bienvenida: config?.mensaje_bienvenida ?? '',
      mensaje_pedido: config?.mensaje_pedido ?? '',
      publicado: config?.publicado ?? false,
    });
  }

  private mensajeError(error: unknown, defecto: string): string {
    const respuesta = error as { error?: { message?: string | string[] } };
    const mensaje = respuesta?.error?.message;
    if (Array.isArray(mensaje)) return mensaje.join(', ');
    return mensaje || defecto;
  }
}
